/** Trimmed Megaventory unit string, or null when unset. */
export function normalizeMrpSkuUnit(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}

/** True when the unit is empty or the default each-style count (Megaventory `Each`, shorthand `ea`). */
export function isMrpSkuDefaultUnit(raw: string | null | undefined): boolean {
  const unit = normalizeMrpSkuUnit(raw);
  if (unit == null) return true;
  const lower = unit.toLowerCase();
  return lower === "ea" || lower === "each";
}

/** Unit label to show beside a SKU, or null when the default each unit should stay implicit. */
export function mrpSkuDisplayUnitLabel(raw: string | null | undefined): string | null {
  const unit = normalizeMrpSkuUnit(raw);
  if (unit == null || isMrpSkuDefaultUnit(unit)) return null;
  return unit;
}

/** Unit label for purchase order line tables (shows Each when unset). */
export function formatMrpPurchaseOrderLineUnit(raw: string | null | undefined): string {
  return normalizeMrpSkuUnit(raw) ?? "Each";
}

const QUANTITY_UNIT_SUFFIX: Record<string, string> = {
  "pound(s)": "lbs",
  pound: "lbs",
  pounds: "lbs",
  lb: "lbs",
  lbs: "lbs",
  "gram(s)": "g",
  gram: "g",
  grams: "g",
  "kilogram(s)": "kg",
  kilogram: "kg",
  kg: "kg",
  each: "each",
  ea: "each",
  "piece(s)": "each",
  "unit(s)": "each",
  "inch(es)": "in",
  feet: "ft",
  foot: "ft",
};

/** Short unit shown after a counted quantity. Megaventory `Pound(s)` is `lbs`. Blank is `each`. */
export function quantityUnitSuffix(raw: string | null | undefined): string {
  const unit = normalizeMrpSkuUnit(raw);
  if (unit == null) return "each";
  return QUANTITY_UNIT_SUFFIX[unit.toLowerCase()] ?? unit;
}

export function formatQuantityWithUnit(quantityText: string, raw: string | null | undefined): string {
  return `${quantityText} ${quantityUnitSuffix(raw)}`;
}

/** Plain-text SKU plus optional unit suffix for tables, search, and print views. */
export function formatMrpSkuWithUnit(sku: string, unit?: string | null): string {
  const trimmedSku = sku.trim();
  if (trimmedSku === "") return "—";
  const unitLabel = mrpSkuDisplayUnitLabel(unit);
  if (unitLabel == null) return trimmedSku;
  return `${trimmedSku} · ${unitLabel}`;
}
